import { Box } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, Tooltip, Typography, Menu, MenuItem, CircularProgress } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import "./AIBot.css";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Button } from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useEffect, useRef, useState, useLayoutEffect, useCallback } from "react";
import ElephantIcon from "../../assessts/images/ElephantIcon.png";
import VisibileIcon from "../../assessts/images/VisibleIcon.png";
import DownloadIcon from "../../assessts/images/DownloadIcon.png";
import { networkUrls } from "../../services/networkurls";
import { Get, Post, PostChat } from "../../services/apiServices";
import { getChatSessionMessages, saveChatMessage, updateChatMessage, createChatSession } from "../../services/chatSessionService";
import constants, { dislikeOptions } from "../../utils/constants";
import CancelIcon from "@mui/icons-material/Cancel";
import EmailIcon from "@mui/icons-material/Email";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import { Chip } from '@mui/material';
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import ShareIcon from "@mui/icons-material/Share";
import ReuseableButton from "../../components/Button/ReusableButton";
import SuggestionCarousel from "../../components/Carousel/SuggestionCarousel";
import ReactDOM from "react-dom";
import { ArrowDropDown } from "@mui/icons-material";
import ReuseableSnackbar from "../../components/Snackbar/Snackbar";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import { saveAs } from "file-saver";
import MobileShareSheet from "../../components/ReusableModal/MobileShareSheet";
import RaiseTicketModal from "../../components/RaiseTicketModal/RaiseTicketModal";
import DocumentSummaryModal from "../../components/DocumentSummaryModal/DocumentSummaryModal";
import { getJwt } from "../../utils/getJwt";
import { title } from "process";
import jwtDecoder from "../../utils/jwtDecoder";
import { text } from "stream/consumers";
import CertificationsTable from "../../components/CertificationsTable/CertificationsTable";
import SummaryWithClientNamesModal from "../../components/SummaryWithClientNamesModal/SummaryWithClientNamesModal";
import React from "react";
import { marked } from "marked";

marked.use({ gfm: true, breaks: true });

const renderMarkdown = (text: string): string => {
  if (!text) return "";
  const cpptMatch = text.match(/<span class="__cppt__"[^>]*>[^<]*<\/span>/);
  const clean = text.replace(/<span class="__cppt__"[^>]*>[^<]*<\/span>/g, "");
  let html = marked.parse(clean) as string;
  html = html.replace(/<a[^>]*>(.*?)<\/a>/gs, "$1");
  return cpptMatch ? html + cpptMatch[0] : html;
};

const PAGE_LIMIT = 20;

// Simple Carousel Component - Guaranteed to work
// True Sliding Carousel Component - One by One Scrolling
const SimpleCarousel = ({ suggestions, onSuggestionClick }: { 
  suggestions: string[], 
  onSuggestionClick: (suggestion: string) => void 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoScrollPaused, setIsAutoScrollPaused] = useState(false);
  const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Items per view based on screen size
  const getItemsPerView = () => {
    if (window.innerWidth <= 768) return 1; // Mobile
    if (window.innerWidth <= 1024) return 2; // iPad  
    return 3; // Desktop
  };
  
  const itemsPerView = getItemsPerView();
  const maxIndex = Math.max(0, suggestions.length - itemsPerView);
  
  // Auto-scroll logic - moves one item at a time
  const startAutoScroll = useCallback(() => {
    if (suggestions.length <= itemsPerView) return; // Don't auto-scroll if all items fit
    
    autoScrollIntervalRef.current = setInterval(() => {
      setCurrentIndex(prev => {
        const nextIndex = prev + 1;
        return nextIndex > maxIndex ? 0 : nextIndex; // Loop back to start
      });
    }, 3000); // Auto-scroll every 3 seconds
  }, [suggestions.length, itemsPerView, maxIndex]);

  const stopAutoScroll = () => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  };

  const pauseAutoScroll = () => {
    setIsAutoScrollPaused(true);
    stopAutoScroll();
    
    // Clear any existing pause timeout
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    // Resume auto-scroll after 6 seconds of inactivity
    pauseTimeoutRef.current = setTimeout(() => {
      setIsAutoScrollPaused(false);
    }, 6000);
  };

  // Start auto-scroll when component mounts or when pause ends
  useEffect(() => {
    if (!isAutoScrollPaused && suggestions.length > itemsPerView) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }

    return () => {
      stopAutoScroll();
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, [isAutoScrollPaused, startAutoScroll, suggestions.length, itemsPerView]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopAutoScroll();
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current);
      }
    };
  }, []);
  
  const goToPrevious = () => {
    pauseAutoScroll();
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };
  
  const goToNext = () => {
    pauseAutoScroll();
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
  };

  const handleSuggestionClick = (suggestion: string) => {
    pauseAutoScroll();
    onSuggestionClick(suggestion);
  };

  // Don't render if no suggestions
  if (!suggestions || suggestions.length === 0) {
    return null;
  }

  const showArrows = suggestions.length > itemsPerView;
  
  // Calculate button width based on items per view
  const buttonWidth = `calc((100% - ${(itemsPerView - 1) * 6}px) / ${itemsPerView})`;
  
  // Calculate the exact scroll distance including gap
  const scrollDistance = 100 / itemsPerView + (6 * 100) / (100 * itemsPerView - (itemsPerView - 1) * 6);

  return (
    <div style={{
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden',
      boxSizing: 'border-box'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '6px', // Reduced gap to prevent overflow
        width: '100%',
        maxWidth: '100%',
        padding: '6px', // Reduced padding
        backgroundColor: '#e3f2fd',
        border: '2px solid #1976d2',
        borderRadius: '12px',
        minHeight: '60px',
        boxSizing: 'border-box',
        position: 'relative',
        overflow: 'hidden' // Prevent any overflow
      }}>
        {/* Auto-scroll indicator */}
        {!isAutoScrollPaused && showArrows && (
          <div style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            width: '8px',
            height: '8px',
            backgroundColor: '#4caf50',
            borderRadius: '50%',
            animation: 'pulse 2s infinite',
            zIndex: 1
          }} />
        )}

        {/* Left Arrow */}
        {showArrows && (
          <button 
            onClick={goToPrevious}
            onMouseEnter={() => pauseAutoScroll()}
            disabled={currentIndex === 0}
            style={{
              backgroundColor: currentIndex === 0 ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '32px', // Reduced size
              height: '32px',
              cursor: currentIndex === 0 ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              transition: 'all 0.3s ease',
              zIndex: 2
            }}
          >
            ←
          </button>
        )}
        
        {/* Carousel Container */}
        <div style={{
          flex: 1,
          overflow: 'hidden',
          position: 'relative',
          minWidth: 0 // Important: allows flex item to shrink
        }}>
          {/* Sliding Track */}
          <div style={{
            display: 'flex',
            gap: '6px', // Reduced gap
            transform: `translateX(-${currentIndex * scrollDistance}%)`,
            transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'transform'
          }}>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                onMouseEnter={() => pauseAutoScroll()}
                style={{
                  width: buttonWidth,
                  minWidth: buttonWidth,
                  maxWidth: buttonWidth,
                  backgroundColor: '#ffffff',
                  border: '2px solid #1976d2',
                  borderRadius: '20px',
                  padding: '6px 10px', // Reduced padding
                  color: '#1976d2',
                  fontSize: '12px', // Reduced font size
                  fontWeight: '500',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  minHeight: '32px', // Reduced height
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'all 0.3s ease',
                  flexShrink: 0
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#1976d2';
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(25, 118, 210, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                  e.currentTarget.style.color = '#1976d2';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                title={suggestion}
              >
                {suggestion}
              </button>
            ))}
          </div>
        </div>
        
        {/* Right Arrow */}
        {showArrows && (
          <button 
            onClick={goToNext}
            onMouseEnter={() => pauseAutoScroll()}
            disabled={currentIndex >= maxIndex}
            style={{
              backgroundColor: currentIndex >= maxIndex ? '#ccc' : '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '32px', // Reduced size
              height: '32px',
              cursor: currentIndex >= maxIndex ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              flexShrink: 0,
              transition: 'all 0.3s ease',
              zIndex: 2
            }}
          >
            →
          </button>
        )}
        
        {/* Progress Indicator */}
        {showArrows && (
          <div style={{
            position: 'absolute',
            bottom: '2px', // Moved up slightly
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            gap: '3px', // Reduced gap
            zIndex: 1
          }}>
            {Array.from({ length: maxIndex + 1 }, (_, i) => (
              <div
                key={i}
                style={{
                  width: '5px', // Reduced size
                  height: '5px',
                  borderRadius: '50%',
                  backgroundColor: i === currentIndex ? '#1976d2' : '#ccc',
                  transition: 'all 0.3s ease'
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


const AIChatBox = ({ open, sessionId, onUpdateSessionTitle,isNewChatClicked,setIsNewChatClicked, onCreateNewSession }: any) => {
  const BaseURL = process.env.REACT_APP_BASE_URL?.replace(/\/$/, "")
  const role = constants.client_side_storage.userDetails?.role;
const[isRecording,setIsRecording]=useState(false);
const[isTranscribing,setIsTranscribing]=useState(false);
const mediaRecorderRef = useRef<MediaRecorder | null>(null);
const audioChunksRef = useRef<Blob[]>([]);
// Rolling window of recent mic amplitude samples (0-1), newest last — drives the waveform bars.
const [waveformLevels, setWaveformLevels] = useState<number[]>([]);
const audioContextRef = useRef<AudioContext | null>(null);
const analyserRef = useRef<AnalyserNode | null>(null);
const waveformFrameRef = useRef<number | null>(null);
// Set by cancelRecording() so the recorder's onstop handler discards instead of transcribing.
const isCancelledRef = useRef(false);
const WAVEFORM_BAR_COUNT = 27;


  const scrollRef = useRef<any>(null);
  const restoreScrollRef = useRef<{ previousHeight: number } | null>(null);
  // Tracks the truly-active session id synchronously — the `sessionId` prop lags
  // by a render cycle right after a lazily-created session is selected, which would
  // otherwise make handleSend think the user switched sessions mid-request.
  const activeSessionIdRef = useRef<string | null>(sessionId ?? null);
  // created_at of the oldest currently-loaded message — the cursor for fetching the next older page.
  const oldestCursorRef = useRef<string | null>(null);
  const [messages, setMessages] = useState<any>([]);
  const [isAppendingMessage, setIsAppendingMessage] = useState(false);
  const [isTicketModal, setIsTicketModal] = useState(false);
  const[isSummaryRequired,setIsSummaryRequired]=useState(false)
  const[isSummaryRequiredMsg,setIsSummaryRequiredMsg]=useState("")
  const [input, setInput] = useState("");
  const[backInputValue,setBackInputValue]=useState("")
  const [isShowSnackbar, setIsShowSnackbar] = useState(false);
  const [direction, setDirection] = useState<"left" | "right">("left");
  const [copied, setCopied] = useState<{ [key: number]: boolean }>({});
  const [showDislikeOptions, setShowDislikeOptions] = useState<Record<string, boolean>>({});
  const [raiseQueryData, setRaiseQueryData] = useState<any>({ chatId: "", userMessage: "" })



  const [snackbarInfo, setSnackbarInfo] = useState({
    color: "",
    message: "",
  });
  const [isMobileShareOpen, setIsMobileShareOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [isSummaryModalOpen, setIsSummaryModalOpen] = useState(false);
  const [documentSummary, setDocumentSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const [openDropdownKey, setOpenDropdownKey] = useState<null | {
    msgIndex: number;
    docIndex: number;
  }>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const iconRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const [dislikeReason, setDislikeReason] = useState<{
    [key: number]: string | null;
  }>({});

  const [loading, setLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [page, setPage] = useState(1);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [isFirstMessage, setIsFirstMessage] = useState(true);
  const [othersReason, setOthersReason] = useState<string>("");
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [anchorEl, setAnchorEl] = useState<Record<number, HTMLElement | null>>({});
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
    const userId = constants.client_side_storage.userDetails?.id;
  const userName = constants.client_side_storage.userDetails?.username;
  const [loadingMessage, setLoadingMessage] = useState<string>("");
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const loadingSessionsRef = useRef<Map<string, boolean>>(new Map()); // Track loading state per session
  const loadingMessages = [
    "Processing your query — this will just take a moment",
    "Working on it… finding the best matches for you.",
    "Gathering the best information for you...",
    "Scanning case studies and use-case videos",
    "Generating an intelligent response for your query",
    "Almost there, compiling results...",
  ];

const handleShareEmail = (doc: any) => {
    const subject = "Shared Search Results from ABSYZ Pre-Sales Portal";
    const body = `Hi there,

Please find attached the document containing the search results you requested via the ABSYZ Pre-Sales Portal.

Preview Link: ${doc.link}

Regards,
Pre-sales Team`;

    const gmailComposeUrl =
      `https://mail.google.com/mail/?view=cm&fs=1` +
      `&su=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    window.open(gmailComposeUrl, "_blank");
  };
  useEffect(() => {
    activeSessionIdRef.current = sessionId ?? null;
  }, [sessionId]);


const stopWaveformSampling = () => {
  if (waveformFrameRef.current !== null) {
    cancelAnimationFrame(waveformFrameRef.current);
    waveformFrameRef.current = null;
  }
  analyserRef.current = null;
  if (audioContextRef.current) {
    audioContextRef.current.close().catch(() => {});
    audioContextRef.current = null;
  }
  setWaveformLevels([]);
};

// Reads live mic amplitude via the Web Audio API and appends it to a rolling
// window of samples — that window is what renders as the waveform bars.
const startWaveformSampling = (stream: MediaStream) => {
  const audioContext = new AudioContext();
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 64;
  audioContext.createMediaStreamSource(stream).connect(analyser);
  audioContextRef.current = audioContext;
  analyserRef.current = analyser;

  const dataArray = new Uint8Array(analyser.frequencyBinCount);
  let lastSampleTime = 0;

  const sample = (time: number) => {
    if (!analyserRef.current) return;
    // Throttle to ~12 samples/sec — smooth enough to read as live, cheap enough for React state.
    if (time - lastSampleTime > 80) {
      lastSampleTime = time;
      analyserRef.current.getByteFrequencyData(dataArray);
      const average = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length;
      const level = Math.min(1, average / 128);
      setWaveformLevels((prev) => {
        const next = [...prev, level];
        return next.length > WAVEFORM_BAR_COUNT ? next.slice(next.length - WAVEFORM_BAR_COUNT) : next;
      });
    }
    waveformFrameRef.current = requestAnimationFrame(sample);
  };
  waveformFrameRef.current = requestAnimationFrame(sample);
};

const startRecording=async()=>{
  try{
    const stream= await navigator.mediaDevices.getUserMedia({audio:true});
    const recorder= new MediaRecorder(stream);
    audioChunksRef.current = [];
    isCancelledRef.current = false;
    recorder.ondataavailable=(e)=>audioChunksRef.current.push(e.data);
    recorder.onstop=()=>{
      stream.getTracks().forEach((track)=>track.stop())
      stopWaveformSampling();
      if (!isCancelledRef.current) {
        handleTranscription();
      }
    }
    recorder.start();
    mediaRecorderRef.current=recorder;
    startWaveformSampling(stream);
    setIsRecording(true);


  }
  catch(err){
 console.error("Mic permission denied or unavailable:", err);
    setSnackbarInfo({ color: "error", message: "Microphone access denied." });
    setIsShowSnackbar(true);
  }
}


const handleTranscription = async () => {
  setIsTranscribing(true);
  const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
  const formData = new FormData();
  formData.append("audio", audioBlob, "recording.webm");

  const response = await PostChat(networkUrls.speechToText, formData, true);
  if (response?.data?.text) {
    setInput((prev) => (prev ? `${prev} ${response.data.text}` : response.data.text));
  }
  setIsTranscribing(false);
};


const stopRecording=()=>{
  isCancelledRef.current = false;
  mediaRecorderRef.current?.stop();
  setIsRecording(false);
}

// Discards the in-progress recording — recorder.onstop still fires (to release the
// mic/stream) but skips transcription because isCancelledRef is set first.
const cancelRecording = () => {
  isCancelledRef.current = true;
  mediaRecorderRef.current?.stop();
  setIsRecording(false);
  audioChunksRef.current = [];
}

useEffect(() => {
  return () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stream.getTracks().forEach((track) => track.stop());
    }
    stopWaveformSampling();
  };
}, []);

  const initChatForSession = React.useCallback(async () => {
  !isNewChatClicked && setHistoryLoading(true);

  let initialMessages: any[] = [];
  // Stays false on a failed fetch (res === null) rather than defaulting to "fully
  // loaded" — otherwise a transient error would silently and permanently block
  // scroll-triggered pagination for the rest of the session.
  let allLoadedAfterInit = false;
  oldestCursorRef.current = null;
  if (sessionId) {
    const res = await getChatSessionMessages(sessionId, { limit: PAGE_LIMIT });
    if (res?.data) {
      const rows = res.data.messages ?? [];
      initialMessages = rows.map((m: any) => ({ ...m.content, id: m.id }));
      allLoadedAfterInit = !res.data.has_more;
      if (rows.length > 0) oldestCursorRef.current = rows[0].created_at;
    }
  }

  if (initialMessages.length > 0) {
    setMessages(initialMessages);
    // If session already has messages, it's not the first message anymore
    setIsFirstMessage(false);
  } else {
    // Start with empty chat for fresh session
    setMessages([]);
    // This is a new session, so next message will be the first
    setIsFirstMessage(true);
  }
  setAllLoaded(allLoadedAfterInit);

  setHistoryLoading(false);
  setIsNewChatClicked(false);

  setTimeout(() => {
    scrollToBottom(false);
    setTimeout(() => scrollToBottom(false), 150);
  }, 0);
}, [sessionId, userName]);

  // Handle loading messages with delay
  useEffect(() => {
    if (loading) {
      let messageIndex = 0;
      
      // Clear any existing timeout
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      
      // Show first message after 3 seconds
      loadingTimeoutRef.current = setTimeout(() => {
        setLoadingMessage(loadingMessages[messageIndex]);
        
        // Rotate through messages every 4 seconds
        const interval = setInterval(() => {
          messageIndex = (messageIndex + 1) % loadingMessages.length;
          setLoadingMessage(loadingMessages[messageIndex]);
        }, 4000);
        
        // Store interval ref for cleanup
        intervalRef.current = interval;
      }, 3000);
    } else {
      // Clear timeout and interval when loading stops
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      setLoadingMessage("");
    }
    
    // Cleanup on unmount
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [loading]);

  useEffect(() => {
  if (!sessionId) return;

  // reset everything for new session
  setMessages([]);
  setPage(1);
  setAllLoaded(false);
  
  // Check if this session is currently loading
  const isSessionLoading = loadingSessionsRef.current.get(sessionId) || false;
  
  if (isSessionLoading) {
    // Restore loading state for this session
    setLoading(true);
    // Restart loading messages immediately
    let messageIndex = 0;
    setLoadingMessage(loadingMessages[messageIndex]);
    
    // Clear any existing timers first
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Start rotating messages
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 4000);
    intervalRef.current = interval;
  } else {
    // Clear loading state for non-loading sessions
    setLoading(false);
    setLoadingMessage("");
    // Clear any pending timeouts/intervals
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }
  
  // Don't reset isFirstMessage here - let initChatForSession handle it based on message history

  initChatForSession();
}, [sessionId, initChatForSession]);



  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !document
          .getElementById("share-dropdown")
          ?.contains(event.target as Node)
      ) {
        setOpenDropdownKey(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !document
          .getElementById("share-dropdown")
          ?.contains(event.target as Node)
      ) {
        setOpenDropdownKey(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (openDropdownKey) {
      const key = `${openDropdownKey.msgIndex}-${openDropdownKey.docIndex}`;
      const ref = iconRefs.current[key];
      if (ref) {
        const rect = ref.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
        });
      }
    } else {
      setDropdownPos(null);
    }
  }, [openDropdownKey]);



  const scrollToBottom = (smooth = false) => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
    }
  };


  const handleCopy = (msg: any, idx: number) => {
    let textToCopy = "";

    if (msg.text) {
      textToCopy += `${msg.text.replace(/<[^>]+>/g, '').trim()}\n\n`;
    }

    if (msg.documents?.length > 0) {
      textToCopy += "Documents used:\n";
      msg.documents.forEach((doc: any) => {
        textToCopy += `- ${doc.filename}\n  Preview: ${doc.preview_link}\n`;
      });
      textToCopy += "\n";
    }

    if (msg?.summary?.summary) {
      textToCopy += `Summary:\n${msg?.summary?.summary}\n\n`;
    }

   

     if (msg?.summary?.common_problems.length > 0) {
      textToCopy += "Common Pain Points:\n";
      msg?.summary?.common_problems.forEach((problem: any) => {
        textToCopy += `- ${problem}\n`;
      });
      textToCopy += "\n";
    }

     if (msg?.summary?.common_solutions?.length > 0) {
      textToCopy += "Implemented Solutions:\n";
      msg?.summary?.common_solutions?.forEach((solution: any) => {
        textToCopy += `- ${solution}\n`;
      });
      textToCopy += "\n";
    }

     if (msg?.key_insights?.length > 0) {
      textToCopy += "Key Insights:\n";
      msg.key_insights.forEach((insight: any) => {
        textToCopy += `- ${insight}\n`;
      });
      textToCopy += "\n";
    }

     if (msg?.values?.length > 0 && msg?.category) {
      textToCopy += `${msg.category}:\n`;
      msg?.values.forEach((value: any) => {
        textToCopy += `- ${value}\n`;
      });
      textToCopy += "\n";
    }

    if (msg?.matched_documents?.length > 0) {
      textToCopy += "Documents used:\n";
      msg?.matched_documents.forEach((doc: any) => {
        textToCopy += `- ${doc.filename}\n`;
      });
      textToCopy += "\n";
    }

    navigator.clipboard
      .writeText(textToCopy.trim())
      .then(() => {
        setCopied((prev) => ({ ...prev, [idx]: true }));
        setTimeout(() => {
          setCopied((prev) => ({ ...prev, [idx]: false }));
        }, 2000);
      })
      .catch((err) => console.error("Failed to copy:", err));
  };

  // Messages need a stable id the moment they're rendered (feedback buttons key off
  // it), but the real id only exists after the backend row is created. Assign a
  // temporary id up front so the UI is immediately interactive, then swap in the
  // real persisted id once the save resolves.
  const genLocalId = () => `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

  const appendAndSaveMessage = (targetSessionId: string, role: "user" | "bot", msgObject: any) => {
    const tempId = genLocalId();
    const msgWithId = { ...msgObject, id: tempId };
    setMessages((prev: any) => [...prev, msgWithId]);
    saveChatMessage(targetSessionId, role, msgWithId).then((res: any) => {
      const realId = res?.data?.id;
      if (!realId) return;
      setMessages((prev: any) =>
        prev.map((m: any) => (m.id === tempId ? { ...m, id: realId } : m))
      );
    });
    return msgWithId;
  };

  const getCategoryShortForm = (category: string): string => {
    const map: Record<string, string> = {
      "case study": "CS",
      "case studies": "CS",
      "casestudy": "CS",
      "case_study": "CS",
      "capability deck": "CD",
      "capability decks": "CD",
      "capabilitydeck": "CD",
      "sows / capability decks": "SOW",
      "sow": "SOW",
      "sows": "SOW",
      "standard sow": "SOW",
      "standard sows": "SOW",
      "statement of work": "SOW",
      "discovery questionnaire": "DQ",
      "discovery questionnaires": "DQ",
      "video": "VID",
      "videos": "VID",
      "demo video": "VID",
      "demo videos": "VID",
      "demo asset": "DA",
      "demo assets": "DA",
      "certification": "CERT",
      "certifications": "CERT",
      "proposal": "PROP",
      "proposals": "PROP",
      "presentation": "PPT",
      "presentations": "PPT",
      "agentforce/ai use cases": "AGNT",
      "agentforce use cases": "AGNT",
      "agentforce": "AGNT",
      "ai use cases": "AI",
      "use case": "UC",
      "use cases": "UC",
      "battle card": "BC",
      "battle cards": "BC",
      "battlecard": "BC",
      "whitepaper": "WP",
      "white paper": "WP",
      "datasheet": "DS",
      "data sheet": "DS",
      "brochure": "BR",
      "one pager": "OP",
      "one-pager": "OP",
    };
    const shortForm = map[category.toLowerCase()];
    if (shortForm) return shortForm;
    // Truncate unmapped categories to avoid overflowing the pill
    return category.length > 6 ? category.slice(0, 5).toUpperCase() + "…" : category.toUpperCase();
  };

  const getGoogleDriveDownloadLink = (link: string, filename: string): string => {
    try {
      const match = link.match(/\/d\/([^/?]+)/);
      if (match && match[1]) {
        const fileId = match[1];
        const ext = filename.split('.').pop()?.toLowerCase() || 'pptx';
        if (link.includes('docs.google.com/presentation')) {
          return `https://docs.google.com/presentation/d/${fileId}/export/${ext}`;
        } else if (link.includes('docs.google.com/document')) {
          return `https://docs.google.com/document/d/${fileId}/export/${ext}`;
        } else if (link.includes('docs.google.com/spreadsheets')) {
          return `https://docs.google.com/spreadsheets/d/${fileId}/export?format=${ext}`;
        }
      }
    } catch {}
    return link;
  };

  const getSlideViewUrl = (link: string, page: number | null | undefined): string => {
    if (!link || !page || !link.includes('docs.google.com/presentation')) return link;
    // If the URL already has a slide-specific anchor, trust it (set by uploader per-slide deep-link)
    if (link.includes('#slide=id.')) return link;
    const base = link.split('?')[0].split('#')[0];
    return `${base}#slide=id.p${page}`;
  };

  const isVideoKblink = (doc: any, msgType?: string): boolean =>
    msgType === "videos" || Boolean(doc?.stream_url);

  const getSourcesLabel = (msgType?: string): string => {
    switch (msgType) {
      case "videos":
        return "Videos";
      case "case_studies":
        return "Case Studies";
      case "industry_tracks":
        return "Industry Tracks";
      default:
        return "Sources";
    }
  };

  // Passing a windowFeatures string (e.g. "noreferrer") as window.open's 3rd
  // argument makes the browser open a stripped-down popup window instead of a
  // normal tab — so get the noopener/noreferrer security property by clearing
  // `opener` afterward instead, which keeps this a plain new tab.
  const openInNewTab = (url: string) => {
    const win = window.open(url, "_blank");
    if (win) win.opener = null;
  };

  const handleViewClick = (e: React.MouseEvent, doc: any) => {
    e.preventDefault();
    openInNewTab(getSlideViewUrl(doc.link, doc.page));
  };

  const formatDisplayName = (filename: string | undefined | null): string => {
    if (!filename) return "";
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
    let name = nameWithoutExt.replace(/[_\-]+/g, " ");
    name = name.replace(/([a-z])([A-Z])/g, "$1 $2");
    return name.replace(/\s+/g, " ").trim();
  };

  const resolveDocCategory = (doc: any): string | null => {
    // Check filename extension FIRST — most reliable for videos
    const name: string = (doc.filename || doc.file_name || doc.original_filename || "").toLowerCase();
    if (name.endsWith(".mp4")) return "VID";
    // If source is industry_track, use category field directly
    if (doc.source === "industry_track") {
      console.log(doc,"docc")
      if (!doc.category) return null;
      return getCategoryShortForm(doc.category);
    }
    // If source is case_study, show CS
    if (doc.source === "case_study") return "CS";
    // Fallback for other sources
    const raw =
      doc.category ||
      doc.doc_type ||
      doc.document_type ||
      doc.file_type ||
      doc.type_label ||
      null;
    if (raw) return getCategoryShortForm(raw);
    return null;
  };

  const downloadFile = async (doc: any, messageIndex: number, docIndex: number) => {
    // Create a unique key for this specific document instance
    const downloadKey = `${messageIndex}-${docIndex}-${doc.drive_file_id}`;
    setDownloadingFiles((prev) => new Set(prev).add(downloadKey));
    const jwt = await jwtDecoder();
    try {
      const res = await fetch(`${BaseURL}${networkUrls.downloadCaseStudy}/${doc.drive_file_id}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Authorization": `Bearer ${jwt}`,  // 👈 add token here
        },
      });

      if (!res.ok) throw new Error("Download failed");

      const blob = await res.blob();

      let filename = doc.filename;
      if (!filename.includes(".")) {
        const contentType = res.headers.get("Content-Type");
        if (contentType?.includes("pdf")) filename += ".pdf";
        else if (contentType?.includes("presentationml")) filename += ".pptx";
        else if (contentType?.includes("spreadsheetml")) filename += ".xlsx";
      }

      saveAs(blob, filename);
    } catch (err) {
      console.error("Download error:", err);
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(downloadKey);
        return newSet;
      });
    }
  };

  const fetchDocumentSummary = async (doc: any) => {
    setSummaryLoading(true);
    setIsSummaryModalOpen(true);
    setDocumentSummary(null);
    try {
      const docId = doc.doc_id || doc.drive_file_id;
      const response = await PostChat(networkUrls.documentSummary, { doc_id: docId });
      if (response?.status === 200) {
        setDocumentSummary({
          ...response.data,
          filename: doc.original_filename || doc.filename,
        });
      } else {
        setDocumentSummary({
          filename: doc.original_filename || doc.filename,
          summary: "Summary not available for this document.",
        });
      }
    } catch (error) {
      console.error("Error fetching document summary:", error);
      setDocumentSummary({
        filename: doc.original_filename || doc.filename,
        summary: "Failed to load summary. Please try again later.",
      });
    } finally {
      setSummaryLoading(false);
    }
  };




  useEffect(() => {
    if (isAppendingMessage) {
      scrollToBottom(true);
      setIsAppendingMessage(false);
    }
  }, [messages]);

useEffect(() => {
  if (!sessionId) {
    // Even if there's no session, we should still fetch queries for suggestions
    // fetchQueries();
    setMessages([]);
    // No session yet (e.g. right after "New Chat") — the next message sent will be
    // the first of a brand-new session, so it must be treated as the first message
    // even if the previous session already had a title/history.
    setIsFirstMessage(true);
    // Clear loading states
    setLoading(false);
    setLoadingMessage("");
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return;
  }

  // 🔥 Reset state on session change
  setMessages([]);
  setPage(1);
  setAllLoaded(false);
  
  // Check if this session is currently loading
  const isSessionLoading = loadingSessionsRef.current.get(sessionId) || false;
  
  if (isSessionLoading) {
    // Restore loading state for this session
    setLoading(true);
    // Restart loading messages immediately
    let messageIndex = 0;
    setLoadingMessage(loadingMessages[messageIndex]);
    
    // Clear any existing timers first
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    // Start rotating messages
    const interval = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingMessage(loadingMessages[messageIndex]);
    }, 4000);
    intervalRef.current = interval;
  } else {
    // Clear loading state for non-loading sessions
    setLoading(false);
    setLoadingMessage("");
    // Clear any pending timeouts/intervals
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }
  // Don't reset isFirstMessage here - let initChatForSession handle it based on message history

  

  initChatForSession();
  // fetchQueries();

}, [sessionId, isTicketModal]);


  // const fetchQueries = async () => {
  //   try {
  //     const queriesResponse = await Get(
  //       `${networkUrls.fetchQueries}?user_id=${userId}&limit=10&similarity_threshold=80&role=${role}`,
  //       true
  //     );

  //     if (queriesResponse?.status === 200) {
  //       const querySuggestions = queriesResponse?.data?.suggestions
  //       setSuggestions(querySuggestions || [])
  //     } else {
  //       setSuggestions([])
  //     }
  //   } catch (error) {
  //     setSuggestions([])
  //   }
  // }

  const handleScroll = async () => {
    if (scrollRef.current.scrollTop < 10 && !loadingOlder && !allLoaded && sessionId) {
      setLoadingOlder(true);
      restoreScrollRef.current = { previousHeight: scrollRef.current.scrollHeight };

      const res = await getChatSessionMessages(sessionId, {
        limit: PAGE_LIMIT,
        before: oldestCursorRef.current ?? undefined,
      });

      if (res?.data) {
        const rows = res.data.messages ?? [];
        if (rows.length > 0) {
          oldestCursorRef.current = rows[0].created_at;
          setMessages((prev: any) => [...rows.map((m: any) => ({ ...m.content, id: m.id })), ...prev]);
        } else {
          restoreScrollRef.current = null;
        }
        setAllLoaded(!res.data.has_more);
        setPage((p) => p + 1);
      } else {
        // Request failed — leave `allLoaded` untouched so the next scroll-to-top
        // can retry, instead of getting silently and permanently locked out.
        restoreScrollRef.current = null;
      }

      setLoadingOlder(false);
    }
  };

  useLayoutEffect(() => {
    if (restoreScrollRef.current && scrollRef.current) {
      const newHeight = scrollRef.current.scrollHeight;
      scrollRef.current.scrollTop =
        newHeight - restoreScrollRef.current.previousHeight;
      restoreScrollRef.current = null;
    }
  }, [messages]);

  useEffect(() => {
    const node = scrollRef.current;
    if (node) {
      node.addEventListener("scroll", handleScroll);
      return () => node.removeEventListener("scroll", handleScroll);
    }
  }, [scrollRef.current, loadingOlder, page, allLoaded]);

  const handleGenaratePPT = async (
    summary: string,
    title: string,
    format: "pptx" | "docx" = "pptx",
    keyInsights?: string[],
    matchedDocuments?: { filename: string }[]
  ) => {
    const cleanTitle = title.replace(/<[^>]+>/g, "");

    const payload = {
      title: cleanTitle,
      summary,
      key_insights: keyInsights || [],
      matched_documents: matchedDocuments?.map(doc => doc.filename) || [],
      format: format,
    };
    try {
      const endpoint = format === "docx" ? networkUrls.genarateDOCX : networkUrls.genaratePPT;
      const response: any = await Post(
        endpoint,
        payload,
        true,
        false,
        "blob"
      );

      const mimeType = format === "docx" 
        ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        : "application/vnd.openxmlformats-officedocument.presentationml.presentation";
      
      const fileExtension = format === "docx" ? "docx" : "pptx";

      const blob = new Blob([response.data], {
        type: mimeType,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${cleanTitle.replace(/\s+/g, "_")}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(`Error generating ${format.toUpperCase()}`, err);
    }
  };






 const handleSend = async (
   include_client_names: any = null,
  overrideQuery?: string,
  forceSessionId?: string,
  isNewlyCreatedSession?: boolean
) => {
  const finalQuery = overrideQuery ?? input;
  if (!finalQuery.trim()) return;

  const currentSessionId = forceSessionId || sessionId;
  const requestSessionId = currentSessionId; // Capture the session ID at request time

  // If no session exists yet, create one on the backend now (lazy creation on first message)
  if (!currentSessionId) {
    const res = await createChatSession("New Chat");
    const newSession = res?.data;
    if (!newSession) {
      // PostChat already surfaced an error snackbar
      return;
    }
    // Update synchronously — the `sessionId` prop won't reflect this until the
    // parent re-renders, but this request already knows it's the active session.
    activeSessionIdRef.current = newSession.id;
    if (onCreateNewSession) {
      onCreateNewSession(newSession);
    }
    handleSend(include_client_names, overrideQuery, newSession.id, true);
    return;
  }

  // The `sessionId` prop propagating down from the parent after `onCreateNewSession`
  // triggers a concurrent `initChatForSession()` fetch (see the session-change effect),
  // which can race ahead and flip `isFirstMessage` to false the instant the user message
  // lands in the backend — before this response comes back to rename the session. Since
  // we know authoritatively (from the branch above) that this session was just created,
  // don't rely on `isFirstMessage` state alone for the rename check below.
  const isFirstMessageOfSession = isFirstMessage || !!isNewlyCreatedSession;

  setIsAppendingMessage(true);
  setLoading(true);
  loadingSessionsRef.current.set(currentSessionId, true); // Mark this session as loading
  scrollToBottom(true);

  if (!overrideQuery) {
    setInput("");
    setBackInputValue(finalQuery);

    const userMessage = { type: "user", text: finalQuery };
    // ✅ session-aware save
    appendAndSaveMessage(currentSessionId, "user", userMessage);
  }

  try {
    let queryResponse: any = await PostChat(networkUrls.chatQuery, { message: finalQuery, stream: false, session_id: currentSessionId });

    console.log(queryResponse, "query response")

    // ✅ Check if we're still on the same session before updating UI
    if (activeSessionIdRef.current !== requestSessionId) {
      console.log(`Response received for session ${requestSessionId}, but current session is ${sessionId}. Skipping UI update.`);
      // Still save to backend for the correct session, but don't update UI
      let botMsg: any = { type: "bot", text: "" };
      
      if (queryResponse?.data?.type==="summarization-prompt"){
         // Don't show modal if we switched sessions
         setLoading(false);
         loadingSessionsRef.current.delete(currentSessionId);
         return;
      }

      // Process the response to save to backend
      if (queryResponse.data.type === "document_types") {
        botMsg = {
          type: "bot",
          text: "Here are the document categories available in the Pre-Sales Portal:",
          document_types: queryResponse.data.values || [],
        };
      } else if (queryResponse.data.type === "category") {
        const { category, values } = queryResponse.data;
        botMsg = {
          type: "bot",
          category,
          values,
          text: `Available ${category}:`,
        };
      } else if (queryResponse.data.type === "documents" && (queryResponse.data.documents?.length || queryResponse.data.matched_documents?.length)) {
        const docsSource = queryResponse.data.documents?.length
          ? queryResponse.data.documents
          : queryResponse.data.matched_documents;
        botMsg = {
          type: "bot",
          text: queryResponse?.data?.answer || queryResponse?.data?.message || "Found the following documents:",
          documents: (docsSource || []).map((doc: any) => ({
            ...doc,
            preview_link: doc.drive_link,
            drive_file_id: doc.doc_id,
            category: doc.category || null,
          })),
        };
      } else if (queryResponse.data.type === "case-study" && queryResponse.data.matched_documents?.length) {
        // Handle case studies response format
        botMsg = {
          type: "bot",
          text: queryResponse?.data?.answer || queryResponse?.data?.message || "Found the following case studies:",
          documents: queryResponse.data.matched_documents.map((doc: any) => ({
            ...doc,
            preview_link: doc.drive_link,
            drive_file_id: doc.doc_id,
            category: doc.category || null,
          })),
        };
      }
      // else if (queryResponse.data.documents?.length) {
      //   botMsg = {
      //     type: "bot",
      //     text: queryResponse?.data?.message
      //       ? `<b>${queryResponse?.data?.message}</b>`
      //       : "Please select from the documents below.",
      //     documents: queryResponse.data.documents,
      //   };
        
      // } 
      else if (queryResponse?.data?.suggested_documents?.length) {
        botMsg = {
          type: "bot",
          text: queryResponse?.data?.note,
          suggestedDocuments: queryResponse?.data?.suggested_documents,
        };
      } else if (queryResponse?.data?.combined_summary) {
        botMsg = {
          type: "bot",
          text:
            queryResponse.data.combined_summary !==
              "Could not generate summarization due to an error."
              ? queryResponse?.data?.is_fallback
                ? `<b>${queryResponse.data.message}</b>`
                : `<b>${queryResponse.data.title}</b>`
              : "",
          summary: queryResponse.data.combined_summary
            ? queryResponse.data.combined_summary
            : [],
          key_insights: queryResponse.data.combined_summary.key_insights || [],
          matched_documents: queryResponse.data.matched_documents || [],
          common_problems: queryResponse.data.combined_summary.common_problems || [],
          common_solutions: queryResponse.data.combined_summary.common_solutions || [],
          title: queryResponse.data.message || "",
        };
      } else if (queryResponse?.data?.generated_use_case_summary) {
        botMsg = {
          type: "bot",
          text: `<b>${queryResponse.data.note}` || "",
          generated_use_case_summary:
            queryResponse?.data?.generated_use_case_summary,
          title: queryResponse.data.title,
        };
      }
      else if (queryResponse.data.type === "industry-track" || queryResponse.data.type === "certifications") {
        botMsg = {
          type: "bot",
          text:
            queryResponse.data.answer ||
            `Sorry, no ${queryResponse.data.type} were found for your request.`,

        };
      }

      else if (queryResponse.data.type === "certifications_list" || queryResponse.data.type === "certifications_table") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.answer || "Sorry, no certifications were found for your request.",
          certifications: queryResponse.data.certifications || [],
          count: queryResponse.data.count || 0,
        };
      }
      else if (queryResponse.data.type === "industry-track-list") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.answer || "Sorry, no industry track were found for your request.",
          industry_tracks: queryResponse.data.industry_tracks || []
        };
      } else if (queryResponse.data.type === "video-transcript") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.message,
          documents: (queryResponse.data.videos || []).map((doc: any) => ({
            ...doc,
            category: doc.category || "Video",
          })),
        }
      } else if (queryResponse.data.type === "video") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.answer || queryResponse.data.message || "Here are the videos from the portal",
          documents: (queryResponse.data.documents || queryResponse.data.sources || []).map((doc: any) => ({
            ...doc,
            drive_link: doc.drive_link,
            drive_file_id: doc.doc_id,
            category: doc.category || "Video",
          })),
        };
      } else if (queryResponse.data.type === "consolidate_ppt") {
        const _cpptData = { title: queryResponse.data.title, download_url: queryResponse.data.download_url };
        botMsg = {
          type: "bot",
          text: `${queryResponse.data.answer || queryResponse.data.message || ""}<span class="__cppt__" data-info="${encodeURIComponent(JSON.stringify(_cpptData))}" style="display:none"></span>`,
          consolidate_ppt: _cpptData,
        };
      } else if (queryResponse.data.response !== undefined && queryResponse.data.kblinks !== undefined) {
        const fullText: string = queryResponse.data.response || "";
        botMsg = {
          type: "bot",
          text: fullText,
          kblinks: queryResponse.data.kblinks || [],
          sourceType: queryResponse.data.type,
          documentSummaries: queryResponse.data.document_summaries || [],
        };
      }
      else {
        botMsg = {
          type: "bot",
          text:
            queryResponse.data.message ||
            "Sorry, no documents were found for your request.",
        };
      }

      saveChatMessage(currentSessionId, "bot", botMsg);
      
      // Update title if it was the first message
      if (isFirstMessageOfSession && onUpdateSessionTitle && finalQuery.trim()) {
        const newTitle = finalQuery.trim().length > 50
          ? finalQuery.trim().substring(0, 47) + "..."
          : finalQuery.trim();
        onUpdateSessionTitle(currentSessionId, newTitle);
        setIsFirstMessage(false);
      }
      
      return; // Don't update UI
    }

    let botMsg: any = { type: "bot", text: "" };
      if (queryResponse?.data?.type==="confirmation"){
         setIsSummaryRequired(true);
         setIsSummaryRequiredMsg(queryResponse?.data?.answer)
         setBackInputValue(input)
         // Clear loading state before returning
         setLoading(false);
         loadingSessionsRef.current.delete(currentSessionId);
         return;
      }

      if (queryResponse.data.type === "document_types") {
        botMsg = {
          type: "bot",
          text: "Here are the document categories available in the Pre-Sales Portal:",
          document_types: queryResponse.data.values || [],
        };
      } else if (queryResponse.data.type === "category") {
        const { category, values } = queryResponse.data;
        botMsg = {
          type: "bot",
          category,
          values,
          text: `Available ${category}:`,
        };
      }
      else if (queryResponse.data.type === "documents" && (queryResponse.data.documents?.length || queryResponse.data.matched_documents?.length)) {
        const docsSource = queryResponse.data.documents?.length
          ? queryResponse.data.documents
          : queryResponse.data.matched_documents;
        botMsg = {
          type: "bot",
          text: queryResponse?.data?.answer || queryResponse?.data?.message || "Found the following documents:",
          documents: (docsSource || []).map((doc: any) => ({
            ...doc,
            preview_link: doc.drive_link,
            drive_file_id: doc.doc_id,
            category: doc.category || null,
          })),
        };
      }
      else if (queryResponse.data.documents?.length) {
        botMsg = {
          type: "bot",
          text: queryResponse?.data?.message
            ? `<b>${queryResponse?.data?.message}</b>`
            : "Please select from the documents below.",
          documents: queryResponse.data.documents.map((doc: any) => ({
            ...doc,
            category: doc.category || doc.doc_type || doc.document_type || null,
          })),
        };
      } 
      else if (queryResponse?.data?.suggested_documents?.length) {
        botMsg = {
          type: "bot",
          text: queryResponse?.data?.note,
          suggestedDocuments: queryResponse?.data?.suggested_documents,
        };
      } else if (queryResponse?.data?.combined_summary) {
        botMsg = {
          type: "bot",
          text:
            queryResponse.data.combined_summary !==
              "Could not generate summarization due to an error."
              ? queryResponse?.data?.is_fallback
                ? `<b>${queryResponse.data.message}</b>`
                : `<b>${queryResponse.data.title}</b>`
              : "",
          summary: queryResponse.data.combined_summary
            ? queryResponse.data.combined_summary
            : [],
          key_insights: queryResponse.data.combined_summary.key_insights || [],
          matched_documents: queryResponse.data.matched_documents || [],
          common_problems: queryResponse.data.combined_summary.common_problems || [],
          common_solutions: queryResponse.data.combined_summary.common_solutions || [],
          title: queryResponse.data.message || "",
        };
      } else if (queryResponse?.data?.generated_use_case_summary) {
        botMsg = {
          type: "bot",
          text: `<b>${queryResponse.data.note}` || "",
          generated_use_case_summary:
            queryResponse?.data?.generated_use_case_summary,
          title: queryResponse.data.title,
        };
      }
      else if (queryResponse.data.type === "industry-track" || queryResponse.data.type === "certifications") {
        botMsg = {
          type: "bot",
          text:
            queryResponse.data.answer ||
            `Sorry, no ${queryResponse.data.type} were found for your request.`,

        };
      }

      else if (queryResponse.data.type === "certifications_list" || queryResponse.data.type === "certifications_table") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.answer || "Sorry, no certifications were found for your request.",
          certifications: queryResponse.data.certifications || [],
          count: queryResponse.data.count || 0,
        };
      }
      else if (queryResponse.data.type === "industry-track-list") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.answer || "Sorry, no industry track were found for your request.",
          industry_tracks: queryResponse.data.industry_tracks || []
        };
      } else if (queryResponse.data.type === "video-transcript") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.message,
          documents: (queryResponse.data.videos || []).map((doc: any) => ({
            ...doc,
            category: doc.category || "Video",
          })),
        }
      } else if (queryResponse.data.type === "video") {
        botMsg = {
          type: "bot",
          text: queryResponse.data.answer || queryResponse.data.message || "Here are the videos from the portal",
          documents: (queryResponse.data.documents || queryResponse.data.sources || []).map((doc: any) => ({
            ...doc,
            drive_link: doc.drive_link,
            drive_file_id: doc.doc_id,
            category: doc.category || "Video",
          })),
        };
      } else if (queryResponse.data.type === "consolidate_ppt") {
        const _cpptData = { title: queryResponse.data.title, download_url: queryResponse.data.download_url };
        botMsg = {
          type: "bot",
          text: `${queryResponse.data.answer || queryResponse.data.message || ""}<span class="__cppt__" data-info="${encodeURIComponent(JSON.stringify(_cpptData))}" style="display:none"></span>`,
          consolidate_ppt: _cpptData,
        };
      } else if (queryResponse.data.response !== undefined && queryResponse.data.kblinks !== undefined) {
        const fullText: string = queryResponse.data.response || "";
        botMsg = {
          type: "bot",
          text: fullText,
          kblinks: queryResponse.data.kblinks || [],
          sourceType: queryResponse.data.type,
          documentSummaries: queryResponse.data.document_summaries || [],
        };
      }
      else {
        botMsg = {
          type: "bot",
          text:
            queryResponse.data.message ||
            "Sorry, no documents were found for your request.",
        };
      }

      appendAndSaveMessage(currentSessionId, "bot", botMsg);

      // Update session title after first successful response
      if (isFirstMessageOfSession && onUpdateSessionTitle && finalQuery.trim()) {
        // Truncate the query to a reasonable title length (e.g., 50 characters)
        const newTitle = finalQuery.trim().length > 50
          ? finalQuery.trim().substring(0, 47) + "..."
          : finalQuery.trim();
        onUpdateSessionTitle(currentSessionId, newTitle);
        setIsFirstMessage(false);
      }
  } catch (error) {
    // ✅ Check if we're still on the same session
    if (activeSessionIdRef.current !== requestSessionId) {
      console.log(`Error occurred for session ${requestSessionId}, but current session is ${sessionId}. Skipping UI update.`);
      const errorMsg = { type: "bot", text: "Error fetching results." };
      saveChatMessage(currentSessionId, "bot", errorMsg);
      
      if (isFirstMessageOfSession && onUpdateSessionTitle && finalQuery.trim()) {
        const newTitle = finalQuery.trim().length > 50
          ? finalQuery.trim().substring(0, 47) + "..."
          : finalQuery.trim();
        onUpdateSessionTitle(currentSessionId, newTitle);
        setIsFirstMessage(false);
      }
      return; // Don't update UI
    }
    
    const errorMsg = { type: "bot", text: "Error fetching results." };
    appendAndSaveMessage(currentSessionId, "bot", errorMsg);

    // Update session title after first response (even if error)
    if (isFirstMessageOfSession && onUpdateSessionTitle && finalQuery.trim()) {
      const newTitle = finalQuery.trim().length > 50
        ? finalQuery.trim().substring(0, 47) + "..."
        : finalQuery.trim();
      onUpdateSessionTitle(currentSessionId, newTitle);
      setIsFirstMessage(false);
    }
  } finally {
    setLoading(false);
    loadingSessionsRef.current.delete(currentSessionId); // Clear loading state for this session
  }
};

  const handleKeyPress = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend(null);
    }
  };



  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  

 

 

 

  useEffect(() => {
    const handleScroll = () => {
      if (openDropdownKey) {
        setOpenDropdownKey(null);
      }
    };

    const scrollContainer = document.querySelector(".scroll-ref-height");
    scrollContainer?.addEventListener("scroll", handleScroll);

    return () => {
      scrollContainer?.removeEventListener("scroll", handleScroll);
    };
  }, [openDropdownKey]);

  // Persists a feedback/content change for an already-saved message. Silently
  // no-ops on a message that hasn't finished its initial save yet (still
  // carrying a local- temp id) — the rare double-click-before-save-lands case.
  const persistMessageUpdate = (updatedMsg: any) => {
    const targetSessionId = activeSessionIdRef.current || sessionId;
    if (!targetSessionId || !updatedMsg?.id || String(updatedMsg.id).startsWith("local-")) return;
    updateChatMessage(targetSessionId, updatedMsg.id, updatedMsg).catch((err: any) =>
      console.error("Failed to save feedback:", err)
    );
  };

  const handleFeedback = async (
    msgIndex: number,
    type: "like" | "dislike",
    msgId?: string
  ) => {
    if (!msgId) return;

    const current = messages[msgIndex];
    if (!current) return;

    const alreadySelected = current.feedback === type;
    const newFeedback = alreadySelected ? null : type;
    const updatedMsg = {
      ...current,
      feedback: newFeedback,
      dislike_reason: newFeedback === "dislike" ? current.dislike_reason : null,
    };

    setMessages((prevMessages: any) =>
      prevMessages.map((msg: any, idx: number) => (idx === msgIndex ? updatedMsg : msg))
    );

    if (type === "like" && !alreadySelected) {
      setDislikeReason((prev) => ({ ...prev, [msgIndex]: null }));
      setShowDislikeOptions((prev) => ({ ...prev, [msgId]: false }));
    }

    if (type === "dislike" && !alreadySelected) {
      setOthersReason("");
      setShowDislikeOptions((prev) => ({ ...prev, [msgId]: true }));
    }

    persistMessageUpdate(updatedMsg);
  };

  const raiseQuery = (id: number) => {
    const userMessage = messages.filter((msg: any) => msg.id === id - 1)[0]?.text
    setRaiseQueryData({ chatId: id, userMessage: userMessage })
    setIsTicketModal(true)

  }



  const handleDislikeReason = async (
    msgId: string,
    reason?: string | null,
    idx?: number
  ) => {
    if (idx === undefined || idx === null) return;

    setDislikeReason((prev: any) => ({
      ...prev,
      [idx]: reason,
    }));
    // "Others" alone (the option button) just reveals the free-text box below —
    // nothing to persist until it's actually submitted with real text.
    if (!reason || reason === "Others") return;

    const current = messages[idx];
    if (!current) return;
    const updatedMsg = { ...current, feedback: "dislike", dislike_reason: reason };

    setMessages((prevMessages: any) =>
      prevMessages.map((msg: any, i: number) => (i === idx ? updatedMsg : msg))
    );
    setShowDislikeOptions((prev) => ({ ...prev, [msgId]: false }));
    setOthersReason("");
    persistMessageUpdate(updatedMsg);
  };

  const handleCloseDislike = (msgId: string, idx: number) => {
    setShowDislikeOptions((prev) => ({ ...prev, [msgId]: false }));
    setDislikeReason((prev) => ({ ...prev, [idx]: null }));
  };


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "78vh",
        overflowX: "hidden",
        "@media (max-width: 768px)": {
          height: "82vh",
          position: "relative",
          overflow: "hidden",
        }
      }}
      className="ai-chat-main-container"
    >
      {historyLoading && (
        <div className="loading-overlay">
          <div className="loading-container">
            <div className="dotted-loader">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            {/* <div className="loading-text">
              Fetching your chat history, please wait...
            </div> */}
          </div>
        </div>
      )}

      <Box
        ref={scrollRef}
        className="scroll-ref-height"
        sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", padding: "16px", width: "100%" }}
      >
        <div className="messages">
          {loadingOlder && (
            <div className="top-loader">
              <span className="dot"></span>
              <span className="dot"></span>
              <span className="dot"></span>
            </div>
          )}
          {messages?.length === 0 && !historyLoading && (
            <div className="message-row bot">
              <img
                src={ElephantIcon}
                alt="Bot"
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              />
              <div className="message left">
                <div>
                  {`Hi${
                    constants.client_side_storage.userDetails?.first_name
                      ? `, ${constants.client_side_storage.userDetails.first_name}`
                      : ""
                  }, welcome to the Pre-Sales Portal! Ask me anything about our case studies, videos, or industry tracks.`}
                </div>
              </div>
            </div>
          )}
          {messages?.map((msg: any, idx: number) => (
            <div
              key={idx}
              className={`message-row ${msg.type === "user" ? "user" : "bot"}`}
            >
              {msg.type === "bot" && (
                <img
                  src={ElephantIcon}
                  alt="Bot"
                  style={{
                    height: "32px",
                    width: "32px",
                    borderRadius: "50%",
                    marginRight: "10px",
                  }}
                />
              )}
              <div
                className={`message ${msg.type === "user" ? "right" : "left"}`}
              >
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.text) }} />



                {msg.documents && (
                  <div className="doc-button-container">
                    {msg.documents.map((doc: any, index: number) => {
                      const refKey = `${idx}-${index}`;
                      const isOpen =
                        openDropdownKey?.msgIndex === idx &&
                        openDropdownKey?.docIndex === index;
                      const downloadKey = `${idx}-${index}-${doc.drive_file_id}`;
                      const isDownloading = downloadingFiles.has(downloadKey);

                      return (
                        <div className="doc-pill" key={index}>
                          {resolveDocCategory(doc) && (
                            <span className="doc-category-badge">
                              {resolveDocCategory(doc)}
                            </span>
                          )}
                          <Tooltip title={doc.original_filename || doc.filename || doc.file_name} placement="top">
                            <span
                              className="doc-name"
                              style={{ cursor: "pointer" }}
                              onClick={() => fetchDocumentSummary(doc)}
                            >
                              {formatDisplayName(doc.original_filename || doc.filename || doc.file_name)}
                            </span>
                          </Tooltip>
                          <div className="vertical-divider"></div>
                          <div className="doc-icons">
                            {/* <a
                              href={doc.download_link}
                              target="_blank"
                              rel="noreferrer"
                            > */}
                            {isDownloading ? (
                              <CircularProgress size={20} sx={{ color: "#4D8FAC", margin: "4px" }} />
                            ) : (
                              <Tooltip title="Download" placement="top">
                                <img
                                  src={DownloadIcon}
                                  className="icon"
                                  style={{ cursor: "pointer" }}
                                  alt="Download"
                                  onClick={() => downloadFile(doc, idx, index)}
                                />
                              </Tooltip>
                            )}
                            {/* </a> */}
                            <div className="vertical-divider"></div>
                            <Tooltip title="View" placement="top">
                              <a
                                href={doc.drive_link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={VisibileIcon}
                                  className="icon visible"
                                  alt="Preview"
                                />
                              </a>
                            </Tooltip>
                            <div className="vertical-divider"></div>

                            <div
                              className="share-wrapper"
                              style={{ position: "relative" }}
                            >
                              <Tooltip title="Share" placement="top">
                                <div
                                  className="share-container"
                                  ref={(el) => {
                                    iconRefs.current[refKey] = el;
                                  }}
                                  onClick={() => {
                                    if (window.innerWidth <= 768) {
                                      setSelectedDoc(doc);
                                      setIsMobileShareOpen(true);
                                    } else {
                                      if (isOpen) {
                                        setOpenDropdownKey(null);
                                      } else {
                                        setOpenDropdownKey({
                                          msgIndex: idx,
                                          docIndex: index,
                                        });
                                      }
                                    }
                                  }}
                                >
                                  <ShareIcon className="icon visible" />
                                </div>
                              </Tooltip>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* 🔽 Dropdown rendered once per open state */}
                    {openDropdownKey &&
                      dropdownPos &&
                      ReactDOM.createPortal(
                        <div
                          id="share-dropdown"
                          className="share-dropdown"
                          onMouseDown={(e) => e.stopPropagation()}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            position: "absolute",
                            top: `${dropdownPos.top}px`,
                            left: `${dropdownPos.left}px`,
                            zIndex: 9999,
                            backgroundColor: "white",
                            border: "1px solid #ccc",
                            padding: "6px 12px",
                            borderRadius: "6px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                            width: "max-content",
                          }}
                        >
                          <div
                            className="share-option"
                            onClick={() => {
                              handleShareEmail(
                                msg.documents[openDropdownKey.docIndex]
                              );
                              setOpenDropdownKey(null);
                            }}
                          >
                            <EmailIcon
                              className="dropdown-icon"
                              sx={{
                                width: "30px",
                                height: "30px",
                                color: "#0088FF",
                              }}
                            />
                            <span>Email</span>
                          </div>
                        </div>,
                        document.body
                      )}
                  </div>
                )}
{msg.kblinks && msg.kblinks.length > 0 && !(msg.documentSummaries && msg.documentSummaries.length > 0) && (
  <>
    <div className="kblinks-label">{getSourcesLabel(msg.sourceType)}:</div>
    <div className="doc-button-container">
    {msg.kblinks.map((doc: any, index: number) => (
      <div className="doc-pill" key={index}>
        <Tooltip title={doc.name} placement="top">
          <span
            className="doc-name"
            style={{ cursor: "pointer" }}
            onClick={() => fetchDocumentSummary(doc)}
          >
            {formatDisplayName(doc.name)}
          </span>
        </Tooltip>
        <div className="vertical-divider"></div>
        <div className="doc-icons">
          <Tooltip title="Download" placement="top">
            <a
              href={doc.download_url || getGoogleDriveDownloadLink(doc.link, doc.name)}
              target="_blank"
              rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center" }}
            >
              <img src={DownloadIcon} style={{ width: 18, height: 18, cursor: "pointer" }} alt="Download" />
            </a>
          </Tooltip>
          <div className="vertical-divider"></div>
          {(() => {
            if (isVideoKblink(doc, msg.sourceType)) {
              const tooltipText = doc.timestamp_label
                ? `Watch from ${doc.timestamp_label}`
                : "Watch video";
              return (
                <Tooltip title={tooltipText} placement="top">
                  <span
                    style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
                    onClick={() => {
                      if (!doc.stream_url) return;
                      openInNewTab(doc.stream_url);
                    }}
                  >
                    <img src={VisibileIcon} style={{ width: 18, height: 18, cursor: "pointer" }} alt="Watch" />
                  </span>
                </Tooltip>
              );
            }

            return (
              <Tooltip title="View" placement="top">
                <a
                  href={getSlideViewUrl(doc.link, doc.page)}
                  onClick={(e) => handleViewClick(e, doc)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ display: "inline-flex", alignItems: "center" }}
                >
                  <img src={VisibileIcon} style={{ width: 18, height: 18, cursor: "pointer" }} alt="Preview" />
                </a>
              </Tooltip>
            );
          })()}
          <div className="vertical-divider"></div>
          <Tooltip title="Send via Email" placement="top">
            <span
              style={{ display: "inline-flex", alignItems: "center", cursor: "pointer" }}
              onClick={() => handleShareEmail(doc)}
            >
              <MailOutlineIcon sx={{ width: 18, height: 18, color: "#0088ff" }} />
            </span>
          </Tooltip>
        </div>
      </div>
    ))}
  </div>
  </>
)}

{msg.documentSummaries && msg.documentSummaries.length > 0 && (
  <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "12px" }}>
    {msg.documentSummaries.map((doc: any, index: number) => (
      <div
        key={index}
        style={{
          border: "1px solid #b3d8ff",
          borderRadius: "10px",
          padding: "14px 16px",
          backgroundColor: "#f8fbff",
        }}
      >
        {doc.summary && (
          <div style={{ marginBottom: "8px" }}>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#0088FF", marginBottom: "4px" }}>Summary</div>
            <div style={{ fontSize: "13px", lineHeight: 1.6 }}>{doc.summary}</div>
          </div>
        )}
        {doc.key_insights?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#0088FF", marginBottom: "4px" }}>Key Insights</div>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {doc.key_insights.map((item: string, i: number) => (
                <li key={i} style={{ fontSize: "12.5px", lineHeight: 1.6 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {doc.common_problems?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#0088FF", marginBottom: "4px" }}>Common Problems</div>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {doc.common_problems.map((item: string, i: number) => (
                <li key={i} style={{ fontSize: "12.5px", lineHeight: 1.6 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {doc.implemented_solutions?.length > 0 && (
          <div style={{ marginBottom: "8px" }}>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#0088FF", marginBottom: "4px" }}>Implemented Solutions</div>
            <ul style={{ margin: 0, paddingLeft: "18px" }}>
              {doc.implemented_solutions.map((item: string, i: number) => (
                <li key={i} style={{ fontSize: "12.5px", lineHeight: 1.6 }}>{item}</li>
              ))}
            </ul>
          </div>
        )}
        {doc.document_links?.length > 0 && (
          <div>
            <div style={{ fontWeight: 600, fontSize: "12px", color: "#0088FF", marginBottom: "4px" }}>Links</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {doc.document_links.map((link: any, i: number) => (
                <a
                  key={i}
                  href={link.link}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: "12px", color: "#0088FF", textDecoration: "underline" }}
                >
                  {formatDisplayName(link.name)}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
)}

{msg.category && Array.isArray(msg.values) && (
  <div className="category-values">
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
      {msg.values.map((value: any, index: number) => (
        <span
          key={index}
          style={{
            padding: "6px 10px",
            backgroundColor: "#e0f2fe",
            borderRadius: "16px",
            fontSize: "12px",
            border: "1px solid #bae6fd"
          }}
        >
          {value}
        </span>
      ))}
    </div>
  </div>
)}

{msg.document_types && Array.isArray(msg.document_types) && msg.document_types.length > 0 && (
  <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginTop: "12px" }}>
    {msg.document_types.map((docType: string, index: number) => {
      const iconMap: Record<string, string> = {
        "Case Studies": "📄",
        "Videos": "🎬",
        "Certifications": "🏆",
        "SOWs / Capability Decks": "📋",
      };
      const descMap: Record<string, string> = {
        "Case Studies": "Client success stories & project implementations",
        "Videos": "Project demonstration & walkthrough videos",
        "Certifications": "Team and individual Salesforce certifications",
        "SOWs / Capability Decks": "Statements of Work & capability documents",
      };
      return (
        <div
          key={index}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "10px",
            backgroundColor: "#f0f7ff",
            border: "1px solid #b3d8ff",
            borderRadius: "10px",
            padding: "10px 14px",
            minWidth: "200px",
            maxWidth: "260px",
            flex: "1 1 200px",
          }}
        >
          <span style={{ fontSize: "22px", lineHeight: 1 }}>{iconMap[docType] || "📁"}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: "13px", color: "#0055cc" }}>{docType}</div>
            {descMap[docType] && (
              <div style={{ fontSize: "11px", color: "#555", marginTop: "3px", lineHeight: "1.4" }}>
                {descMap[docType]}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
)}

                {msg.generated_use_case_summary && (
                  <>
                    <div style={{ marginTop: "10px" }}>
                      <Button
                        variant="contained"
                        onClick={(e) => setAnchorEl(prev => ({ ...prev, [msg.id]: e.currentTarget }))}
                        sx={{
                          backgroundColor: "#074c90",
                          "&:hover": { backgroundColor: "#063d73" },
                          textTransform: "none",
                        }}
                        endIcon={<ArrowDropDown />}
                      >
                        Generate
                      </Button>
                      <Menu
                        anchorEl={anchorEl[msg.id]}
                        open={Boolean(anchorEl[msg.id])}
                        onClose={() => setAnchorEl(prev => ({ ...prev, [msg.id]: null }))}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem
                          onClick={() => {
                            handleGenaratePPT(
                              msg.generated_use_case_summary,
                              msg.title,
                              "pptx"
                            );
                            setAnchorEl(prev => ({ ...prev, [msg.id]: null }));
                          }}
                        >
                          PPTX
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleGenaratePPT(
                              msg.generated_use_case_summary,
                              msg.title,
                              "docx"
                            );
                            setAnchorEl(prev => ({ ...prev, [msg.id]: null }));
                          }}
                        >
                          DOCX
                        </MenuItem>
                      </Menu>
                    </div>
                  </>
                )}
                {msg.certifications && msg.certifications.length > 0 && (
                  <CertificationsTable certifications={msg.certifications} />
                )}
                {msg.industry_tracks && msg.industry_tracks.length > 0 && (
                  <div className="industry-list" >


                    <ul style={{ paddingLeft: "18px", marginTop: "5px" }}>
                      {msg.industry_tracks.map((industrytrack: any, index: number) => (
                        <li
                          key={index}
                          style={{
                            padding: "4px 0",
                            fontSize: "14px",
                            lineHeight: "1.4"
                          }}
                        >
                          {industrytrack.industry_name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {msg.consolidate_ppt && (
                  <div style={{
                    marginTop: "10px",
                    padding: "12px 16px",
                    backgroundColor: "#f0f7ff",
                    border: "1px solid #b3d8ff",
                    borderRadius: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ fontSize: "22px" }}>📊</span>
                      <span style={{ fontWeight: 600, fontSize: "13px", color: "#0055cc" }}>
                        {msg.consolidate_ppt.title}
                      </span>
                    </div>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        backgroundColor: "#074c90",
                        "&:hover": { backgroundColor: "#063d73" },
                        textTransform: "none",
                        fontSize: "12px",
                        borderRadius: "8px",
                        cursor: "pointer",
                      }}
                      onClick={async () => {
                        const jwt = await jwtDecoder();
                        try {
                          const rawDownloadUrl = msg.consolidate_ppt.download_url?.startsWith("/api/") ? msg.consolidate_ppt.download_url.replace(/^\/api/, "") : msg.consolidate_ppt.download_url;
                          const res = await fetch(`${BaseURL}${rawDownloadUrl}`, {
                            method: "GET",
                            credentials: "include",
                            headers: { "Authorization": `Bearer ${jwt}` },
                          });
                          if (!res.ok) throw new Error("Download failed");
                          const blob = await res.blob();
                          saveAs(blob, msg.consolidate_ppt.title || "consolidated.pptx");
                        } catch (err) {
                          console.error("Consolidated PPT download error:", err);
                        }
                      }}
                    >
                      Download PPT
                    </Button>
                  </div>
                )}

                {msg.suggestedDocuments && (
                  <div className="doc-button-container">
                    {msg.suggestedDocuments.map((doc: any, index: number) => {
                      const downloadKey = `${idx}-${index}-${doc.drive_file_id}`;
                      const isDownloading = downloadingFiles.has(downloadKey);
                      return (
                      <div className="doc-pill" key={index}>
                        {resolveDocCategory(doc) && (
                          <span className="doc-category-badge">
                            {resolveDocCategory(doc)}
                          </span>
                        )}
                        <Tooltip title={doc.original_filename || doc.filename || doc.file_name} placement="top">
                          <span
                            className="doc-name"
                            style={{ cursor: "pointer" }}
                            onClick={() => fetchDocumentSummary(doc)}
                          >
                            {formatDisplayName(doc.original_filename || doc.filename || doc.file_name)}
                          </span>
                        </Tooltip>
                        <div className="vertical-divider"></div>
                        <div className="doc-icons">
                          {/* <a
                            href={doc.download_link}
                            target="_blank"
                            rel="noreferrer"
                          > */}
                          {isDownloading ? (
                            <CircularProgress size={20} sx={{ color: "#4D8FAC", margin: "4px" }} />
                          ) : (
                            <Tooltip title="Download" placement="top">
                              <img
                                src={DownloadIcon}
                                className="icon"
                                style={{ cursor: "pointer" }}
                                alt="Download"
                                onClick={() => downloadFile(doc, idx, index)}
                              />
                            </Tooltip>
                          )}
                          {/* </a> */}
                          <div className="vertical-divider"></div>
                          <Tooltip title="View" placement="top">
                            <a
                              href={doc.preview_link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <img
                                src={VisibileIcon}
                                className="icon visible"
                                alt="Preview"
                              />
                            </a>
                          </Tooltip>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
                {(msg?.summary && msg?.type === "bot") &&
                  msg?.summary !==
                  "Could not generate summarization due to an error." && (
                    <div className="summary-block">
                      <h4 style={{ marginBottom: 0,paddingBottom:4 }}>Summary:</h4>
                      <div className="summary-text">{msg?.summary?.summary}</div>
 {/* Add this for common problems */}
      {msg?.summary?.common_problems?.length > 0 && (
        <>
          <h4 style={{ marginBottom: 0 }}>Common Pain Points:</h4>
          <ul className="key-insights">
            {msg?.summary?.common_problems.map((problem: any, index: any) => (
              <li key={index}>{problem}</li>
            ))}
          </ul>
        </>
      )}

      {/* Add this for common solutions */}
      {msg?.summary?.common_solutions?.length > 0 && (
        <>
          <h4 style={{ marginBottom: 0 }}>Implemented Solutions:</h4>
          <ul className="key-insights">
            {msg?.summary?.common_solutions.map((solution: any, index: any) => (
              <li key={index}>{solution}</li>
            ))}
          </ul>
        </>
      )}
                      {msg?.summary?.key_insights?.length > 0 && (
                        <>
                          <h4 style={{ marginBottom: 0 }}>Key Insights:</h4>
                          <ul className="key-insights">
                            {msg.summary?.key_insights.map(
                              (insight: any, index: any) => (
                                <li key={index}>{insight}</li>
                              )
                            )}
                          </ul>
                        </>
                      )}
                        {msg.matched_documents?.length > 0 && (
                          <>
                            <h4 style={{ marginBottom: 0 }}>
                              Here are the documents that were used to generate
                              the above summary
                            </h4>

                            <ul className="key-insights">
                              {msg.matched_documents.map(
                                (doc: any, index: any) => (
                                  <li key={index}>
                                    <a
                                      href={doc.drive_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        cursor: 'pointer',
                                        color: '#1976d2',
                                        textDecoration: 'underline',
                                        transition: 'color 0.2s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#063d73';
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color = '#1976d2';
                                      }}
                                    >
                                      {doc.original_filename}
                                    </a>
                                  </li>
                                )
                              )}
                            </ul>
                          </>
                        )}

                      <div style={{ marginTop: "10px" }}>
                        <Button
                          variant="contained"
                          onClick={(e) => setAnchorEl(prev => ({ ...prev, [msg.id]: e.currentTarget }))}
                          sx={{
                            backgroundColor: "#074c90",
                            "&:hover": { backgroundColor: "#063d73" },
                            textTransform: "none",
                          }}
                          endIcon={<ArrowDropDown />}
                        >
                          Generate
                        </Button>
                        <Menu
                          anchorEl={anchorEl[msg.id]}
                          open={Boolean(anchorEl[msg.id])}
                          onClose={() => setAnchorEl(prev => ({ ...prev, [msg.id]: null }))}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              handleGenaratePPT(
                                msg.summary,
                                msg.title ? msg.title : msg.text,
                                "pptx",
                                msg.key_insights,
                                msg.matched_documents
                              );
                              setAnchorEl(prev => ({ ...prev, [msg.id]: null }));
                            }}
                          >
                            PPTX
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              handleGenaratePPT(
                                msg.summary,
                                msg.title ? msg.title : msg.text,
                                "docx",
                                msg.key_insights,
                                msg.matched_documents
                              );
                              setAnchorEl(prev => ({ ...prev, [msg.id]: null }));
                            }}
                          >
                            DOCX
                          </MenuItem>
                        </Menu>
                      </div>
                    </div>
                  )}



                {msg.type === "bot" && (
                  <div className="feedback-section">
                    <div className="feedback-buttons">
                      {/* 👍 Like button */}
                      <IconButton
                        onClick={() => handleFeedback(idx, "like", msg.id)}
                        className="feedback-btn"
                      >
                        {msg?.feedback === "like" ? (
                          <ThumbUpIcon style={{ color: "blue" }} />
                        ) : (
                          <ThumbUpOffAltIcon style={{ color: "gray" }} />
                        )}
                      </IconButton>

                      {/* 👎 Dislike button */}
                      <IconButton
                        onClick={() => handleFeedback(idx, "dislike", msg.id)}
                        className="feedback-btn"
                      >
                        {msg?.feedback === "dislike" ? (
                          <ThumbDownAltIcon style={{ color: "red" }} />
                        ) : (
                          <ThumbDownOffAltIcon style={{ color: "gray" }} />
                        )}
                      </IconButton>

                      {/* 📋 Copy button */}
                      <IconButton onClick={() => handleCopy(msg, idx)}>
                        {copied[idx] ? (
                          <CheckIcon fontSize="small" style={{ color: "green" }} />
                        ) : (
                          <ContentCopyIcon fontSize="small" />
                        )}
                      </IconButton>
                      {msg.text &&
                        (msg.text.includes("Sorry, no documents were found for your request") ||
                          msg.text.includes("Sorry,We couldn’t find this document in the portal. However, here is some AI-generated content from the web that may help you.")) && (
                          <>
                            {msg.ticket_id != null ? (
                              <>
                                <Chip
                                  icon={<CheckCircleIcon sx={{ color: "green !important" }} />}
                                  label="Ticket Raised"
                                  sx={{
                                    fontWeight: 600,
                                    mt: 0.5,
                                    bgcolor: "rgba(0, 128, 0, 0.1)",
                                    color: "green",
                                    "& .MuiChip-icon": { color: "green" }
                                  }}
                                />
                              </>
                            ) : (
                              <>
                                <Tooltip title="Raise a Query">
                                  <IconButton
                                    // color="primary"
                                    onClick={() => raiseQuery(msg.id)}
                                    size="small"
                                  >
                                    <HelpOutlineIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                          </>
                        )}

                    </div>

                    {/* 👎 Dislike reason options */}
                    {msg.feedback === "dislike" && showDislikeOptions[msg.id] && (
                      <div className="dislike-options">
                        <div className="dislike-header">
                          <p>Tell us more:</p>
                          <IconButton
                            size="small"
                            onClick={() => handleCloseDislike(msg.id, idx)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </div>

                        <div className="options-container">
                          {dislikeOptions.map((option) => (
                            <Button
                              key={option}
                              variant={
                                dislikeReason[idx] === option ? "contained" : "outlined"
                              }
                              color={
                                dislikeReason[idx] === option ? "primary" : "inherit"
                              }
                              size="small"
                              onClick={() => handleDislikeReason(msg.id, option, idx)}
                              sx={{
                                borderRadius: "16px",
                                textTransform: "none",
                                fontSize: "12px",
                                padding: "2px 8px",
                                minWidth: "unset",
                                height: "28px",
                              }}
                            >
                              {option}
                            </Button>
                          ))}
                        </div>

                        {/* "Others" input */}
                        {dislikeReason[idx] === "Others" && (
                          <div
                            style={{
                              width: "100%",
                              marginTop: "10px",
                              display: "flex",
                            }}
                          >
                            <textarea
                              rows={4}
                              style={{ width: "50%" }}
                              value={othersReason}
                              onChange={(e: any) => setOthersReason(e.target.value)}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              disabled={!othersReason?.trim()}
                              onClick={() =>
                                handleDislikeReason(msg.id, othersReason.trim(), idx)
                              }
                              sx={{
                                marginTop: "6px",
                                textTransform: "none",
                                height: "32px",
                                alignSelf: "end",
                                marginLeft: "10px",
                                fontSize: "12px",
                                padding: "6px 16px",
                              }}
                            >
                              Submit
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}


               
              </div>
            </div>
          ))}

          {loading && (
            <div className="message-row bot">
              <img
                src={ElephantIcon}
                alt="Bot"
                style={{
                  height: "32px",
                  width: "32px",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              />
              <div className="message left">
                {loadingMessage ? (
                  <div className="loading-message-container">
                    <span className="loading-message-text">{loadingMessage}</span>
                    <div className="typing-indicator">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                ) : (
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </Box>

      <Box
        sx={{
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          position: "relative",
          flexDirection: "column",
        }}
      >
        <Box sx={{ width: "100%", display: "flex" }} className="ai-chat-bottom-box">
          <img
            src={ElephantIcon}
            alt="chatbot"
            style={{ height: "36px", marginRight: "10px" }}
          />
          {/* Group textarea and carousel together */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                width: "100%",
                position: "relative",
                display: "flex",
                alignItems: "flex-start",
              }}
            >
              {isRecording ? (
                <Box
                  sx={{
                    width: "100%",
                    height: "40px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "0 10px",
                    backgroundColor: "white",
                    borderRadius: "50px",
                    border: "1px solid #0088FF",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: "24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "3px",
                      overflow: "hidden",
                    }}
                  >
                    {Array.from({ length: WAVEFORM_BAR_COUNT }).map((_, i) => {
                      const level = waveformLevels[i];
                      const barHeight = 4 + (level ?? 0) * 20;
                      return (
                        <Box
                          key={i}
                          sx={{
                            width: "3px",
                            flexShrink: 0,
                            height: `${barHeight}px`,
                            borderRadius: "2px",
                            backgroundColor: "#0088FF",
                            opacity: level === undefined ? 0.25 : 1,
                            transition: "height 0.08s ease",
                          }}
                        />
                      );
                    })}
                  </Box>
                  <IconButton size="small" onClick={cancelRecording} sx={{ color: "#8a94a6" }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={stopRecording} sx={{ color: "#096AD9" }}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <textarea
                    ref={textareaRef}
                    placeholder="Ask any question"
                    className="ai-chat-input"
                    style={{
                      width: "100%",
                      padding: "10px 50px 10px 10px",
                      backgroundColor: "white",
                      borderRadius: "50px",
                      fontSize: "14px",
                      border: "1px solid #0088FF",
                      resize: "none",
                      color: "#6B7895",
                      height: "40px",
                      overflow: "hidden",
                      outline: 0,
                    }}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyPress}
                  />
                  <IconButton
                    sx={{ position: "absolute", right: "50px", top: "50%", transform: "translateY(-50%)", color: "#096AD9" }}
                    disabled={isTranscribing}
                    onClick={startRecording}
                  >
                    {isTranscribing ? <CircularProgress size={18} /> : <MicIcon />}
                  </IconButton>
                  <IconButton
                    className="ai-chat-send-button"
                    sx={{
                      position: "absolute",
                      right: "14px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#096AD9",
                    }}
                    disabled={loading}
                    onClick={() => handleSend(null)}
                  >
                    <SendIcon />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Suggestions Carousel */}
            <Box sx={{ 
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              overflow: 'hidden',
              marginTop: '8px'
            }}>
              <SuggestionCarousel 
                suggestions={suggestions || []} 
                onSuggestionClick={(suggestion: string) => {
                  setInput(suggestion);
                  textareaRef.current?.focus();
                }}
              />
            </Box>

          </Box>

        </Box>
        
      </Box>
      {isShowSnackbar && (
        <ReuseableSnackbar
          isShowSnackbar={isShowSnackbar}
          colour={snackbarInfo.color}
          setIsShowSnackbar={setIsShowSnackbar}
          message={snackbarInfo.message}
          autoHideDuration={snackbarInfo.message.includes("Ctrl+F") ? 6000 : 2000}
        />
      )}
      {isMobileShareOpen && (
        <MobileShareSheet
          open={isMobileShareOpen}
          onClose={() => setIsMobileShareOpen(false)}
          doc={selectedDoc}
        />
      )}
      {isTicketModal && (
        <RaiseTicketModal
          open={isTicketModal}
          onClose={() => setIsTicketModal(false)}
          data={raiseQueryData}
          // setMessages={setMessages}
        />
      )}
      <DocumentSummaryModal
        open={isSummaryModalOpen}
        onClose={() => {
          setIsSummaryModalOpen(false);
          setDocumentSummary(null);
        }}
        documentSummary={documentSummary}
        loading={summaryLoading}
      />

      {isSummaryRequired && <SummaryWithClientNamesModal setOpen={setIsSummaryRequired} open={isSummaryRequired} message={isSummaryRequiredMsg} handleSend={handleSend} backUpQuery={backInputValue} />}
    </Box>
  );
};

export default AIChatBox;
